import { useCallback } from 'react';
import { runInAction } from 'mobx';
import { useNavigate } from 'react-router-dom';
import { QueueDisplayDensityEnum } from '@mark43/cad-api';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { useGoBackToPreviousFirstResponderPage } from '../common/hooks/use-go-back-to-previous-first-responder-page';
import { useWorkspaceStore } from '../panels/use-workspace-store';
import { FR_UNITS_LIST_PATH } from '../router/constants';

/**
 * Single source of truth for the FR units list fullscreen open/close.
 *
 * Three callbacks because we have two distinct close semantics:
 *   - `expandToFullscreen`     — used by the drawer's expand button and the
 *                                 UNITS_LIST_EXPANDED shortcut.
 *   - `collapseFromFullscreen` — used by the page's collapse button. Goes
 *                                 back to the previous FR page and slides
 *                                 the units drawer out alongside it.
 *   - `closeFullscreen`        — used by the UNITS_LIST_EXPANDED shortcut
 *                                 on re-press. Per product (RND-55874), the
 *                                 shortcut should *fully* close — no drawer
 *                                 left behind. The button keeps its
 *                                 drawer-restoring behavior unchanged.
 *
 * Density is mutated via `runInAction` on the workspace queue directly
 * rather than via `QueueStore.setDisplayDensity` so the same hook can be
 * used outside the QueueStateProvider subtree (e.g. RouteManager).
 */
export function useFrUnitsListFullscreen() {
    const navigate = useNavigate();
    const { open: openDrawer, close: closeDrawer } = useDrawerControls();
    const goBackToPreviousFirstResponderPage = useGoBackToPreviousFirstResponderPage();
    const workspaceStore = useWorkspaceStore();

    const expandToFullscreen = useCallback(() => {
        const unitsPanel = workspaceStore
            .getActivePanels()
            .find(
                (panel) =>
                    panel.workspacePanelType === 'QUEUE' && panel.queue?.dataEntityType === 'UNIT'
            );
        if (unitsPanel?.queue) {
            runInAction(() => {
                unitsPanel.queue!.displayDensity = QueueDisplayDensityEnum.COMPACT.name;
            });
        }
        closeDrawer();
        navigate(FR_UNITS_LIST_PATH);
    }, [workspaceStore, closeDrawer, navigate]);

    const collapseFromFullscreen = useCallback(() => {
        goBackToPreviousFirstResponderPage();
        openDrawer({ type: 'fr-units-list', props: {} });
    }, [goBackToPreviousFirstResponderPage, openDrawer]);

    const closeFullscreen = useCallback(() => {
        // Ensure nothing is left visible after re-press.
        closeDrawer();
        goBackToPreviousFirstResponderPage();
    }, [closeDrawer, goBackToPreviousFirstResponderPage]);

    return { expandToFullscreen, collapseFromFullscreen, closeFullscreen };
}
